import * as React from 'react';
import { type DragEvent, type KeyboardEvent, type RefObject, type SyntheticEvent, forwardRef } from 'react';

import { Image, styled } from '@volkswagen-onehub/components-core';
import { Checkmark } from '@volkswagen-onehub/icons-core';

import { KeyCode } from '../../utils/key-code';

const OPTION_BASE_SIZE = 42;

interface OptionsProps {
	ref: RefObject<HTMLButtonElement>;
	ariaLabel: string;
	ariaBusy: boolean;
	onClick: () => void;
	selectedOption?: boolean;
	altText: string;
	thumbnailUrl: string;
	testId: string;
}

export const Option = forwardRef<HTMLButtonElement, OptionsProps>(
	({ ariaLabel, ariaBusy, onClick, selectedOption, altText, thumbnailUrl, testId }, ref) => {
		function onDragStart(event: DragEvent<HTMLImageElement>) {
			event.preventDefault();
		}

		function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
			const events: Array<string> = [KeyCode.ENTER, KeyCode.SPACE];

			if (events.includes(event.key)) {
				onClick();
			}
		}

		function onOptionClick(event: SyntheticEvent) {
			event.preventDefault();
			event.stopPropagation();

			onClick();
		}

		return (
			<IconWrapper
				ref={ref}
				aria-busy={!selectedOption && !!ariaBusy}
				aria-current={!!selectedOption}
				aria-label={ariaLabel}
				tabIndex={0}
				onClick={onOptionClick}
				onKeyDown={onKeyDown}
				isActive={!!selectedOption}
				data-testid={testId}
			>
				<CheckmarkIcon variant={'small'} isActive={!!selectedOption} aria-hidden={true} />
				<ImageWrapper>
					<Image src={thumbnailUrl} alt={''} key={altText} draggable={false} onDragStart={onDragStart} />
				</ImageWrapper>
			</IconWrapper>
		);
	}
);

Option.displayName = 'Visualizer_Option';

const CheckmarkIcon = styled(Checkmark)<{ isActive: boolean }>`
	display: ${props => (props.isActive ? 'block' : 'none')};
	position: absolute;
	top: -5px;
	right: -5px;
	width: 22px;
	height: 22px;
	padding: 6px;
	color: ${props => props.theme.colors.background.primary};
	background-color: ${({ theme }) => (theme.variant === 'v26' ? '#997F67' : theme.colors.signal.success)};
	border-radius: 50%;
`;

const IconWrapper = styled.button<{ isActive: boolean }>`
	// Prevents chips from being highlighted on drag
	user-select: none;
	position: relative;
	box-sizing: border-box;
	border-radius: 50%;
	border: 2px solid
		${({ theme, isActive }) =>
			isActive
				? theme.variant === 'v26'
					? '#997F67'
					: theme.colors.signal.success
				: theme.colors.background.secondary};
	padding: 2px;
	border-width: ${({ theme }) => (theme.variant === 'v26' ? '1px !important' : '2px')};
	cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
	background-color: ${props => props.theme.colors.background.primary};

	&:focus {
		outline: none;
		border: 2px solid
			${({ theme, isActive }) =>
				isActive ? (theme.variant === 'v26' ? '#997F67' : theme.colors.signal.success) : theme.colors.focus.main};
		border-radius: 50%;
	}
`;

const ImageWrapper = styled.div`
	overflow: hidden;
	background-color: #fff;
	height: ${OPTION_BASE_SIZE}px;
	width: ${OPTION_BASE_SIZE}px;
	border-radius: 50%;
`;
